import type { ReactNode } from 'react';
import { useLocation, useRouter } from '@tanstack/react-router';
import { PageLoader } from '@customerio/pluma-components/react';
import { useCurrentWorkspace } from '@/context/current-workspace';
import Journeys from '@/pages/journeys';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useAuth } from '@/services/auth';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { LastWorkspaceResolver } from '@/routes/-components';
import { objectsLegacyHref, objectsMigrationHref } from './urls';

function locationHref(location: ReturnType<typeof useLocation>): string {
  const hash = location.hash === '' || location.hash.startsWith('#') ? location.hash : `#${location.hash}`;
  return `${location.pathname}${location.searchStr}${hash}`;
}

export function LegacyObjectsHandoff() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ObjectsMigration);
  const location = useLocation();

  if (!isMigrated) return <Journeys />;

  return (
    <SafeNavigate to={objectsMigrationHref(locationHref(location))} options={{ shouldReplaceHistoryEntry: true }} />
  );
}

type ObjectsMigrationRouteProps = { children: ReactNode };

function ObjectsAccessGate({ children }: ObjectsMigrationRouteProps) {
  const { account } = useAuth();
  const router = useRouter();

  if (isBuilderPlan(account)) {
    return (
      <SafeNavigate to="/settings/billing/available-plans/journeys" options={{ shouldReplaceHistoryEntry: true }} />
    );
  }

  return (
    <ProtectedRoute
      permission="journeys/objects/view"
      allowReadOnlyMasqAccess={true}
      redirectTo={(params) =>
        router.buildLocation({ to: '/workspaces/$workspaceId/journeys', params: { workspaceId: params.workspaceId } })
          .href
      }
    >
      {children}
    </ProtectedRoute>
  );
}

export function ObjectsMigrationRoute({ children }: ObjectsMigrationRouteProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ObjectsMigration);
  const location = useLocation();
  const workspace = useCurrentWorkspace();

  if (!isMigrated) {
    return (
      <SafeNavigate to={objectsLegacyHref(locationHref(location))} options={{ shouldReplaceHistoryEntry: true }} />
    );
  }

  if (workspace == null) return <PageLoader label="Loading…" />;

  return (
    <LastWorkspaceResolver>
      <ObjectsAccessGate>{children}</ObjectsAccessGate>
    </LastWorkspaceResolver>
  );
}
